import { MemberInfo } from "@qite/tide-client";
import React, { createContext, useContext, useEffect, useState } from "react";
import { decodeTokenToMemberInfo } from "../../utils/member-and-tide-utils";
import { Query, TideAirline, TideAirport, TideCountry, TideItemForAirline, TideTag } from "../../types";
import { graphql, useStaticQuery } from "gatsby";

interface GlobalProviderProps {
  children: React.ReactNode;
}

export interface GlobalContextType {
  language: string;
  member?: MemberInfo;
  isMobile: boolean;
  isLgScreen: boolean;
  isQsmExpanded: boolean;
  setLanguage: (language: string) => void;
  setMember: (member: MemberInfo | undefined) => void;
  setIsQsmExpanded: (isExpanded: boolean) => void;
  logout?: () => void;
  cmsAirlines: TideItemForAirline[];
  airlines: TideAirline[];
  airports: TideAirport[];
  countries: TideCountry[];
  tags: TideTag[];
}

const initialState: GlobalContextType = {
  language: "",
  member: {} as MemberInfo,
  isMobile: false,
  isLgScreen: false,
  isQsmExpanded: false,
  setLanguage: () => {},
  setMember: () => {},
  setIsQsmExpanded: () => {},
  logout: () => {},
  cmsAirlines: [],
  airlines: [],
  countries: [],
  airports: [],
  tags: [],
};

const GlobalContext = createContext(initialState);

export const GlobalProvider: React.FC<GlobalProviderProps> = (props) => {
  const [language, setLanguage] = useState<string>("");
  const [member, setMember] = useState<MemberInfo | undefined>();
  const [isMobile, setIsMobile] = useState<boolean>(false);
  const [isLgScreen, setIsLgScreen] = useState<boolean>(false);
  const [isQsmExpanded, setIsQsmExpanded] = useState<boolean>(false);
  // // const [cmsAirlines, setCmsAirlines] = useState<TideItemForAirline[]>([]);
  // const [airlines, setAirlines] = useState<TideAirline[]>([]);
  // const [airports, setAirports] = useState<TideAirport[]>(initialState.airports);
  // const [countries, setCountries] = useState<TideCountry[]>(initialState.countries);

  // fetch countries
  const globalQuery = useStaticQuery(graphql`
    query GlobalQuery {
      allTideCountry {
        nodes {
          localizedNames {
            languageCode
            value
          }
          name
          tideId
        }
      }
      allTideItemForAirline {
        nodes {
          content {
            general {
              data {
                iata
              }
            }
            images {
              logoCompact {
                url
                altText
                title
              }
            }
          }
        }
      }
      allTideAirline {
        nodes {
          iata
          name
        }
      }
      allTideAirport {
        nodes {
          iata
          name
          locationName
          country {
            localizedNames {
              languageCode
              value
            }
            name
          }
          localizedNames {
            languageCode
            value
          }
          localizedLocations {
            languageCode
            value
          }
        }
      }
      allTideTag {
        nodes {
          name
          tideId
        }
      }
    }
  `) as Query;

  const cmsAirlines = React.useMemo(() => globalQuery.allTideItemForAirline.nodes, [globalQuery]);
  const countries = React.useMemo(() => globalQuery.allTideCountry.nodes, [globalQuery]);
  const airlines = React.useMemo(() => globalQuery.allTideAirline.nodes, [globalQuery]);
  const airports = React.useMemo(() => globalQuery.allTideAirport.nodes, [globalQuery]);
  const tags = React.useMemo(() => globalQuery.allTideTag.nodes, [globalQuery]);

  // useEffect(() => {
  //   setCountries(globalQuery.allTideCountry.nodes);
  //   // setCmsAirlines(globalQuery.allTideItemForAirline.nodes);
  //   setAirlines(globalQuery.allTideAirline.nodes);
  //   setAirports(globalQuery.allTideAirport.nodes);
  // }, [globalQuery]);

  useEffect(() => {
    const smallScreenQuery = window.matchMedia("(max-width: 768px)");
    const lgScreenQuery = window.matchMedia("(min-width: 769px) and (max-width: 1200px)");

    const handleSmallScreen = () => setIsMobile(smallScreenQuery.matches);
    const handleLgScreen = () => setIsLgScreen(lgScreenQuery.matches);

    // Set initial values
    handleSmallScreen();
    handleLgScreen();

    // Listen for changes
    smallScreenQuery.addEventListener("change", handleSmallScreen);
    lgScreenQuery.addEventListener("change", handleLgScreen);

    return () => {
      smallScreenQuery.removeEventListener("change", handleSmallScreen);
      lgScreenQuery.removeEventListener("change", handleLgScreen);
    };
  }, []);

  useEffect(() => {
    if (typeof localStorage !== "undefined") {
      const sessionToken = localStorage.getItem("token");
      if (sessionToken) {
        const member = decodeTokenToMemberInfo(sessionToken);
        if (member) {
          setMember(member);
        } else {
          localStorage.removeItem("token");
          setMember(undefined);
        }
      }
    }
  }, []);

  const logout = () => {
    localStorage.removeItem("token");
    setMember(undefined);
  };

  return (
    <GlobalContext.Provider
      value={{
        language,
        member,
        isMobile,
        isLgScreen,
        isQsmExpanded,
        setLanguage,
        setMember,
        logout,
        cmsAirlines,
        airlines,
        airports,
        countries,
        setIsQsmExpanded,
        tags,
      }}
    >
      {props.children}
    </GlobalContext.Provider>
  );
};

export default GlobalContext;

export const useGlobal = (): GlobalContextType => {
  const context = useContext(GlobalContext);
  if (!context) throw new Error("useQsm must be used within a QsmProvider");
  return context;
};
